import { ImageRestriction } from 'advanced-cropper';
import { getAbsoluteZoom, getZoomFactor } from 'advanced-cropper/extensions/absolute-zoom';
import { CropperState } from 'advanced-cropper/types';
import { ChangeEvent, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { FaCamera, FaCrop } from 'react-icons/fa';
import { ImZoomIn, ImZoomOut } from 'react-icons/im';
import { VscDebugStepBack } from 'react-icons/vsc';
import { Cropper, CropperRef } from 'react-mobile-cropper';
import ReactModal from 'react-modal';
import styled from 'styled-components';

import 'react-mobile-cropper/dist/style.css';

interface IModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  cropperSize: {
    height: number;
    width: number;
  };
  url?: string;
  /**
   * Method triggered when cut is performed
   * @param url image as `base64`
   * @returns
   */
  onCropImage?: (file: File, url?: string) => void;
  onReset?: () => void;
  resizable?: boolean;
}
export function ModalImageCropper({ open, cropperSize, url, resizable, setOpen, onCropImage, onReset }: IModalProps) {

  const inputRef = useRef<HTMLInputElement>(null);
  const cropperRef = useRef<CropperRef>();

  const [image, setImage] = useState<string>();
  const [photo, setPhoto] = useState<File>();

  const [cropImageState, setCropImageState] = useState<CropperState | null>();
  const [cropImageSettings, setCropImageSettings] = useState<any>();

  const [mult, setMult] = useState(0);

  const zoom = (action?: 'In' | 'Out') => {
    if (!(cropImageState && cropImageSettings)) return;
    const absoluteZoom = getAbsoluteZoom(cropImageState, cropImageSettings);
    const z_size = 0.01;

    cropperRef?.current?.zoomImage(
      getZoomFactor(cropImageState, cropImageSettings, action === 'Out' ? absoluteZoom - z_size * mult : absoluteZoom + z_size),
    );
    if (action === 'Out') {
      setMult((prev) => prev + 1);
    } else setMult(0);
  };

  const reset = () => cropperRef?.current?.reset();

  const onUpload = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const onCrop = () => {
    const cropper = cropperRef.current;
    if (cropper) {
      const canvas = cropper.getCanvas();
      if (canvas) {
        const dataURL = canvas.toDataURL();
        fetch(dataURL)
          .then((res) => res.blob())
          .then((blob) => {
            const type = photo?.type.split('/');
            const file = new File([blob], `${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${type?.[type?.length - 1]}`, {
              type: photo?.type,
            });
            handlerCloseModal();
            onCropImage?.(file, dataURL);
            setImage('');
          });
      }
    }
  };

  const onLoadImage = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      setImage(URL.createObjectURL(file));
      setPhoto(file);
    }
    event.target.value = '';
  };

  useEffect(() => {
    // Revoke the object URL, to allow the garbage collector to destroy the uploaded before file
    return () => {
      if (image) {
        URL.revokeObjectURL(image);
      }
    };
  }, [image]);

  const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-40%',
      transform: 'translate(-50%, -50%)',
      padding: 0,
    },
    overlay: {
      zIndex: 999,
    },
  };

  const handlerCloseModal = () => {
    setOpen((prev) => !prev);
    setImage('');
  };

  return (
    <ReactModal isOpen={open} style={customStyles}>
      <Container>
        <Content>
          {image || url ? (
            <div className="modal__cropper-wrapper">
              <Cropper
                ref={cropperRef}
                className="modal__cropper"
                backgroundClassName="modal__cropper-background"
                src={image ? image : url}
                /*maxWidth={cropperSize.width}
                maxHeight={cropperSize.height}*/
                imageRestriction={ImageRestriction.none}
                onChange={({ getSettings, getState }: any) => {
                  setCropImageSettings(getSettings());
                  setCropImageState(getState());
                }}
                stencilProps={{
                  movable: false,
                  resizable: !!resizable,
                }}
                defaultSize={{ width: 50, height: 50 }}
                defaultCoordinates={{
                  left: (cropperSize.width * -1) / 2,
                  top: (cropperSize.height * -1) / 2,
                  width: cropperSize.width * 4,
                  height: cropperSize.height * 4,
                }}
              />
            </div>
          ) : null}
          <div className="modal__buttons-wrapper">
            <button className="modal__button" onClick={onUpload}>
              <input ref={inputRef} type="file" accept="image/*" onChange={onLoadImage} />
              <Camera />
              <span>Inserir foto</span>
            </button>
            {image ? (
              <button className="modal__button" onClick={onCrop}>
                <CropAndSave />
                <span>Recortar e Salvar</span>
              </button>
            ) : onReset ? (
              <button className="modal__button modal_reset_button" onClick={onReset}>
                <Revert />
                <span>Desfazer</span>
              </button>
            ) : null}
            <button onClick={handlerCloseModal} className="modal__button modal__button_cancel">
              Cancelar
            </button>
            {image || url ? (
              <div className="modal__zoom">
                <span onClick={() => zoom('Out')}>
                  <ImZoomOut size={20} color="#fff" />
                </span>
                <ResetButton onClick={reset}>
                  <Revert size={20} /> <article>Desfazer</article>
                </ResetButton>
                <span onClick={() => zoom('In')}>
                  <ImZoomIn size={20} color="#fff" />
                </span>
              </div>
            ) : null}
          </div>
        </Content>
      </Container>
    </ReactModal>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: 10px;
  gap: 5px;

  * {
    box-sizing: border-box;
  }

  .modal {
    user-select: none;
    min-width: 300px;

    &__zoom {
      position: absolute;
      top: 15px;
      user-select: none;
      display: flex;
      flex-direction: row;

      & span {
        cursor: pointer;

        &:first-child {
          margin-right: 1rem;
        }
        &:last-child {
          margin-left: 1rem;
        }
      }
    }

    &_noImage {
      padding: 1rem;
      font-size: 4em;
      text-align: center;
      color: rgba(255, 255, 255, 0.2);
    }
    &__cropper-wrapper {
      background: black;
    }

    &__cropper {
      color: #174F68; // cropper's color: ;
      max-height: 70vh;
      height: 70vh;
      width: 70vw;
      margin: 0;
    }

    &__cropper-background {
      background: black;
    }

    &__cropper-wrapper {
      position: relative;
    }

    &__buttons-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      bottom: 0;
      padding: 1rem;
      flex: 1;

      & > *:first-child {
        margin-right: 1rem;
      }
    }

    &_reset_button {
      white-space: pre-wrap;
      & span {
        display: block !important;
        margin-left: 0.3rem;
      }
    }
    &__button {
      border: none;
      outline: solid transparent;
      padding: 12px 25px;
      height: 42px;
      background-color: #174F68;
      font-family: 'Nunito';
      font-style: normal;
      font-weight: 500;
      font-size: 16px;
      outline: none;
      border: none;
      border-radius: 8px;
      color: white;
      cursor: pointer;
      transition: background 0.5s;
      display: flex;
      align-items: center;

      &:hover,
      &:focus {
        background: #20232a;
      }
      input {
        display: none;
      }

      &_cancel {
        background-color: #20232a;
        margin-left: 1rem;

        &:hover,
        &:focus {
          background: #ff44008c;
        }
      }
    }
  }
  @media (max-width: 450px) {
    .modal__buttons-wrapper {
      flex-direction: column;
      gap: 5px;

      & > * {
        width: 100%;
        margin: 0 !important;
      }
    }
  }

  @media (max-width: 600px) {
    width: 100%;

    .modal__button {
      display: flex;
      justify-content: center;
      align-items: center;

      & > *:first-child,
      & > *:nth-child(2) {
        margin-right: 0;
      }

      & > span {
        display: none;
      }
    }
    .modal__cropper {
      width: 90vw;
    }
  }
`;

const ResetButton = styled.span`
  color: #174F68;
  font-weight: bold;
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 10pt;
  margin: 0 1rem;
  text-transform: capitalize;

  & > *:first-child {
    margin-right: 0.5rem;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  padding: 5px;
`;

const Camera = styled(FaCamera)`
  margin-right: 1rem;
`;

const CropAndSave = styled(FaCrop)`
  margin-right: 1rem;
`;

const Revert = styled(VscDebugStepBack)`
  margin-right: 1rem;
`;
